import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchFeaturesData, fetchPricingData } from '../utils/api';
import Skeleton from 'react-loading-skeleton';

function FeaturesTable() {
    const { data: featuresData = [], isLoading: featuresLoading } = useQuery({
        queryKey: ['features'],
        queryFn: fetchFeaturesData
    });

    const { data: pricingData = [], isLoading: pricingLoading, isError: pricingError } = useQuery({
        queryKey: ['pricingData'],
        queryFn: fetchPricingData
    });

    // Skeleton loading UI
    if (featuresLoading || pricingLoading) {
        return (
            <section>
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="pt-12 md:pt-20">
                        {/* Section header */}
                        <div className="pb-12">
                            <h2 className="h2">Features</h2>
                        </div>

                        {/* Skeleton loading */}
                        <div className="overflow-x-auto">
                            <table className="table-auto w-full border-b border-gray-200">
                                <thead>
                                    <tr className="text-base sm:text-lg border-t border-gray-200">
                                        <th className="text-bold text-left pr-2 py-4 min-w-48">Pick Your Plan</th>
                                        {/* Render table header dynamically */}
                                        {[1, 2, 3].map((_, index) => (
                                            <th key={index} className="text-bold text-center px-2 py-4">
                                                <Skeleton width={100} />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                {/* Skeleton loading for table body */}
                                <tbody>
                                    {[1, 2, 3].map((_, rowIndex) => (
                                        <tr key={rowIndex} className="border-t border-gray-200">
                                            <td className="text-sm sm:text-base pr-2 py-4">
                                                <Skeleton width={200} />
                                            </td>
                                            {/* Render table cells dynamically */}
                                            {[1, 2, 3].map((_, cellIndex) => (
                                                <td key={cellIndex} className="text-sm pl-2 py-4 text-center font-medium">
                                                    <Skeleton width={50} height={20} />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (pricingError) return <div>Error fetching pricing data</div>;

    // Exclude the "REST API" feature from display
    const featureModuleNames = featuresData
        .filter(item => item.module_name && item.module_name.toLowerCase() !== 'rest api' && item.module_name.toLowerCase() !== 'restapi')
        .map(item => item.module_name);

    // Pricing descriptions and limits
    const descriptions = pricingData.map(item => item.description);
    const maxEmployees = pricingData.map(item => item.max_employees);
    const maxFileStorage = pricingData.map(item => item.max_storage_size);

    return (
        <section>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
                <div className="pt-12 md:pt-20">
                    {/* Section header */}
                    <div className="pb-12">
                        <h2 className="h2">Features</h2>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="table-auto w-full border-b border-gray-200">
                            <thead>
                                <tr className="text-base sm:text-lg border-t border-gray-200">
                                    <th className="text-bold text-left pr-2 py-4 min-w-48">Pick Your Plan</th>
                                    {/* Render table header dynamically */}
                                    {descriptions.slice(2).map((description, index) => (
                                        <th key={index} className="text-bold text-center px-2 py-4">{description}</th>
                                    ))}
                                </tr>
                            </thead>

                            {/* Table body */}
                            <tbody>
                                {/* Max employees row */}
                                <tr className="border-t border-gray-200">
                                    <td className="text-sm sm:text-base pr-2 py-4">
                                        <div className="font-medium underline uppercase">MAX ACTIVE EMPLOYEES</div>
                                    </td>
                                    {maxEmployees.slice(2).map((emp, idx) => (
                                        <td key={idx} className="text-sm pl-2 py-4 text-center font-medium">{emp}</td>
                                    ))}
                                </tr>

                                {/* Max storage row */}
                                <tr className="border-t border-gray-200">
                                    <td className="text-sm sm:text-base pr-2 py-4">
                                        <div className="font-medium underline uppercase">MAX FILE STORAGE</div>
                                    </td>
                                    {maxFileStorage.slice(2).map((size, idx) => (
                                        <td key={idx} className="text-sm pl-2 py-4 text-center font-medium">{size}GB</td>
                                    ))}
                                </tr>

                                {/* Feature rows */}
                                {featureModuleNames.map((moduleName, rowIndex) => (
                                    <tr key={rowIndex} className="border-t border-gray-200">
                                        <td className="text-sm sm:text-base pr-2 py-4">
                                            <div className="font-medium underline uppercase">{moduleName}</div>
                                        </td>
                                        {descriptions.slice(2).map((description, cellIndex) => {
                                            const hasFeature = pricingData.some(
                                                item =>
                                                    item.description === description &&
                                                    item.module_in_package?.includes(moduleName)
                                            );
                                            return (
                                                <td
                                                    key={cellIndex}
                                                    className="text-sm pl-2 py-4 text-center font-medium"
                                                >
                                                    {hasFeature ? (
                                                        <svg
                                                            className="w-3 h-3 fill-current text-green-500 inline-flex"
                                                            viewBox="0 0 12 12"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            className="w-3 h-3 fill-current text-gray-400 opacity-75 inline-flex"
                                                            viewBox="0 0 12 12"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path d="M10.72 1.293a1 1 0 00-1.415 0L6.012 4.586 2.72 1.293a1 1 0 00-1.414 1.414L4.598 6 1.305 9.293a1 1 0 101.414 1.414l3.293-3.293 3.293 3.293a1 1 0 001.414-1.414L7.426 6l3.293-3.293a1 1 0 000-1.414z" />
                                                        </svg>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default FeaturesTable;
